import * as THREE from 'three';

/**
 * Mikro trawa: ziarno w kolorze terenu, bez jednego trójkąta.
 *
 * **Nic tu nie jest geometrią i to jest cała decyzja.** Gra renderuje
 * w obniżonej rozdzielczości skalowanej całkowicie do okna — źdźbło miałoby
 * w tym buforze jeden, dwa piksele i migotałoby przy każdym ruchu kamery, bo
 * jego krawędź wpadałaby i wypadała z siatki pikseli. Plamka koloru czyta się
 * dokładnie tak, jak ma się czytać, i kosztuje kilka operacji na piksel, który
 * i tak jest rysowany: zero trójkątów, zero instancji, zero pamięci.
 *
 * **Gdzie rośnie, mówi słownik biomów** (`grass` w `biomes.json`), a nie lista
 * po tej stronie — to jest fakt o klimacie, jak kolor. Wchodzi tu atrybutem
 * wierzchołka, więc na granicy łąki z piaskiem trawa gaśnie płynnie: tym samym
 * dzieleniem wierzchołków, które wygładza cieniowanie.
 */

/**
 * Jak drobne jest ziarno, w komórkach szumu na jednostkę świata.
 *
 * **Podłogą jest piksel, nie gust.** Rozstaw pól to `√3 × HEX_SIZE`, czyli
 * około 0,87 jednostki; przy 9 komórka szumu ma jakąś jedną dziewiątą heksa,
 * a heks na ekranie ma kilkanaście do trzydziestu pikseli — plamka wypada więc
 * na dwa, trzy piksele. Drobniej znaczy poniżej piksela, a to nie jest trawa,
 * tylko **migotanie**: plamka wpada i wypada z siatki pikseli przy każdym ruchu
 * kamery. Skala jest w jednostkach świata, a nie ekranu, więc przy oddaleniu
 * ziarno gęstnieje i zlewa się w kolor, zamiast rosnąć razem z obrazem.
 */
const PATCH = 9.0;

/** Druga, rzadsza warstwa — żeby ziarno nie układało się w widoczną kratę. */
const CLUMP = 2.6;

/** O ile plamka rozjaśnia i przyciemnia. Ledwie widoczne z bliska, znikome z daleka. */
const DEPTH = 0.07;

const GRASS_GLSL = /* glsl */ `
  // Wartość szumu z pozycji świata. Ta sama funkcja mieszająca, co wszędzie
  // indziej w tym projekcie: sinus rozbity na ułamek, bez tekstury do wczytania.
  float grassHash(vec2 at) {
    return fract(sin(dot(floor(at), vec2(127.1, 311.7))) * 43758.5453123);
  }

  // Płynnie między czterema sąsiednimi komórkami siatki, żeby plamka miała
  // brzeg, a nie schodek.
  float grassNoise(vec2 at) {
    vec2 cell = floor(at);
    vec2 part = fract(at);
    vec2 weight = part * part * (3.0 - 2.0 * part);

    float a = grassHash(cell);
    float b = grassHash(cell + vec2(1.0, 0.0));
    float c = grassHash(cell + vec2(0.0, 1.0));
    float d = grassHash(cell + vec2(1.0, 1.0));

    return mix(mix(a, b, weight.x), mix(c, d, weight.x), weight.y);
  }
`;

/**
 * Dołóż ziarno trawy do materiału terenu.
 *
 * Owija istniejące `onBeforeCompile`, a nie zastępuje je — teren ma już cień
 * chmur i podmiana zabrałaby mu go po cichu. To ta sama ostrożność, którą
 * `applyCloudShadow` bierze wobec wody.
 */
export function applyGrassDetail(material: THREE.Material): void {
  const before = material.onBeforeCompile;

  material.onBeforeCompile = (shader, renderer) => {
    before?.call(material, shader, renderer);

    shader.vertexShader = shader.vertexShader
      .replace(
        '#include <common>',
        '#include <common>\nattribute float grass;\nvarying float vGrass;\nvarying vec3 vGrassWorld;'
      )
      .replace(
        '#include <begin_vertex>',
        `#include <begin_vertex>
        vGrass = grass;
        vGrassWorld = (modelMatrix * vec4(transformed, 1.0)).xyz;`
      );

    shader.fragmentShader = shader.fragmentShader
      .replace(
        '#include <common>',
        `#include <common>
        varying float vGrass;
        varying vec3 vGrassWorld;
        ${GRASS_GLSL}`
      )
      // W kolorze **przed** światłem: trawa jest tym, jaka ta ziemia jest,
      // a nie tym, jak akurat pada na nią słońce. Po świetle plamki nie
      // gasłyby w nocy razem z resztą łąki.
      .replace(
        '#include <color_fragment>',
        `#include <color_fragment>
        if (vGrass > 0.0) {
          vec2 at = vGrassWorld.xz;
          float speck = grassNoise(at * ${PATCH.toFixed(1)});
          float clump = grassNoise(at * ${CLUMP.toFixed(1)});
          // Dwie warstwy o niewspółmiernej skali: sama drobna układa się
          // w widoczną kratę, sama rzadka wygląda jak plamy wilgoci.
          float shade = (speck - 0.5) * 0.75 + (clump - 0.5) * 0.25;
          diffuseColor.rgb *= 1.0 + shade * ${DEPTH.toFixed(2)} * 2.0 * vGrass;
        }`
      );
  };

  material.needsUpdate = true;
}
